import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Store for cancellation tokens
let currentSearchController = null;
let currentMobileSearchController = null;

// Debounced search function
let searchTimeout = null;
let mobileSearchTimeout = null;

export const searchApi = createAsyncThunk(
  "search/fetchResults",
  async (query, { rejectWithValue, getState, dispatch }) => {
    try {
      // Cancel previous request if it exists
      if (currentSearchController) {
        currentSearchController.abort();
      }

      // Create new AbortController for this request
      currentSearchController = new AbortController();

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/search`, {
        ...config,
        params: { query },
        signal: currentSearchController.signal, // Add cancellation signal
      });

      // Clear the controller after successful request
      currentSearchController = null;
      
      return response.data;
    } catch (error) {
      // Clear the controller on error
      currentSearchController = null;
      
      // Don't reject if the request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ message: 'Search cancelled' });
      }
      
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Debounced search thunk
export const searchApiDebounced = createAsyncThunk(
  "search/fetchResultsDebounced",
  async (query, { dispatch }) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Return a promise that resolves after debounce delay
    return new Promise((resolve) => {
      searchTimeout = setTimeout(() => {
        // Dispatch the actual search
        dispatch(searchApi(query))
          .then((result) => {
            resolve(result);
          })
          .catch((error) => {
            resolve(error);
          });
      }, 300); // 300ms debounce delay
    });
  }
);

// Mobile-optimized search for chat functionality
export const searchMobileApi = createAsyncThunk(
  "search/fetchMobileResults",
  async (query, { rejectWithValue, getState, dispatch }) => {
    try {
      // Cancel previous request if it exists
      if (currentMobileSearchController) {
        currentMobileSearchController.abort();
      }

      // Create new AbortController for this request
      currentMobileSearchController = new AbortController();

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/search/mobile`, {
        ...config,
        params: { query },
        signal: currentMobileSearchController.signal, // Add cancellation signal
      });

      // Clear the controller after successful request
      currentMobileSearchController = null;
      
      return response.data;
    } catch (error) {
      // Clear the controller on error
      currentMobileSearchController = null;
      
      // Don't reject if the request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ message: 'Mobile search cancelled' });
      }
      
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Debounced mobile search thunk
export const searchMobileApiDebounced = createAsyncThunk(
  "search/fetchMobileResultsDebounced",
  async (query, { dispatch }) => {
    // Clear existing timeout
    if (mobileSearchTimeout) {
      clearTimeout(mobileSearchTimeout);
    }

    // Return a promise that resolves after debounce delay
    return new Promise((resolve) => {
      mobileSearchTimeout = setTimeout(() => {
        // Dispatch the actual search
        dispatch(searchMobileApi(query))
          .then((result) => {
            resolve(result);
          })
          .catch((error) => {
            resolve(error);
          });
      }, 300); // 300ms debounce delay
    });
  }
);

// Helper function to cancel ongoing search
export const cancelSearch = () => {
  if (currentSearchController) {
    currentSearchController.abort();
    currentSearchController = null;
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout);
    searchTimeout = null;
  }
};

// Helper function to cancel ongoing mobile search
export const cancelMobileSearch = () => {
  if (currentMobileSearchController) {
    currentMobileSearchController.abort();
    currentMobileSearchController = null;
  }
  if (mobileSearchTimeout) {
    clearTimeout(mobileSearchTimeout);
    mobileSearchTimeout = null;
  }
};
